import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {useCallback} from 'react';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {useCreateSectorMutation, useUpdateSectorMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {ParkingSectorFormSchema, ParkingSectorFormType} from '../schemas/ParkingSectorFormSchema';

type ParkingSectorBase = {
  branchId: string;
  name: string;
  prefix: string;
  numberOfParkingSpaces: number;
};

export type ParkingSectorType = ParkingSectorBase & {
  sectorId: string;
};

export interface ParkingSectorFormProps {
  editData?: ParkingSectorType;
}

export function ParkingSectorForm({editData}: ParkingSectorFormProps) {
  const navigate = useNavigate();
  const [createNewParkingSector, {isLoading: createLoading}] = useCreateSectorMutation();
  const [updateParkingSector, {isLoading: updateLoading}] = useUpdateSectorMutation();
  const {sectorId, ...defaultValues} = editData ?? {};

  const {data: currentUser, isLoading} = useGetCurrentUserInfoQuery();
  const currentUserBranchesOptions = (currentUser?.branches ?? []).map((branch) => ({
    label: branch.name,
    value: branch.id,
  }));

  const handleSubmit = useCallback<FormSubmitHandler<ParkingSectorFormType>>(
    async (data, setErrors, _reset, blockerContext) => {
      if (editData) {
        await updateParkingSector({
          updateSectorRequestBody: {
            branchId: data.branchId,
            name: data.name,
            numberOfSpaces: data.numberOfParkingSpaces,
            prefix: data.prefix,
          },
          sectorId: editData?.sectorId,
        })
          .unwrap()
          .then(() => {
            showNotification.success(
              i18n.t('page.vehicleSettings.notifications.parkingSectorEdited')
            );
            if (blockerContext?.wasSubmittedFromBlocker) {
              return;
            }
            navigate(settingsRoutes.vehicleParking);
          })
          .catch((e: ApiError) =>
            handleApiError(e, {
              callback: ({validationErrors}) => setErrors(validationErrors),
              signalApiError: blockerContext?.reportApiFailure,
            })
          );
      } else {
        await createNewParkingSector({
          createSectorRequestBody: {
            branchId: data.branchId,
            name: data.name,
            numberOfSpaces: data.numberOfParkingSpaces,
            prefix: data.prefix,
            type: 'VEHICLE',
          },
        })
          .unwrap()
          .then(() => {
            showNotification.success(
              i18n.t('page.vehicleSettings.notifications.parkingSectorCreated')
            );
            if (blockerContext?.wasSubmittedFromBlocker) {
              return;
            }
            navigate(settingsRoutes.vehicleParking);
          })
          .catch((e: ApiError) =>
            handleApiError(e, {
              callback: ({validationErrors}) => setErrors(validationErrors),
              signalApiError: blockerContext?.reportApiFailure,
            })
          );
      }
    },
    [createNewParkingSector, updateParkingSector, navigate, editData]
  );

  return (
    <DataStatus isLoading={isLoading}>
      <Form<ParkingSectorFormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={ParkingSectorFormSchema}
        defaultValues={sectorId ? defaultValues : undefined}
        shouldWatchForUnsavedChanges
      >
        {(control, _formApi, navigateWithoutBlocker) => {
          const handleCancel = () => navigateWithoutBlocker(settingsRoutes.vehicleParking);

          return (
            <Card
              title={
                editData ? editData.name : i18n.t('page.vehicleSettings.labels.newParkingSector')
              }
            >
              <VStack spacing={4}>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      type="text"
                      control={control}
                      label={i18n.t('entity.parkingSector.name')}
                      name="name"
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      type="choice"
                      control={control}
                      label={i18n.t('entity.parkingSector.branch')}
                      placeholder={i18n.t('general.labels.select')}
                      name="branchId"
                      options={currentUserBranchesOptions}
                    />
                  </Box>
                </HStack>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      type="text"
                      control={control}
                      label={i18n.t('entity.parkingSector.prefix')}
                      name="prefix"
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      type="number"
                      control={control}
                      label={i18n.t('entity.parkingSector.numberOfParkingSpaces')}
                      name="numberOfParkingSpaces"
                    />
                  </Box>
                </HStack>
                <HStack justify="flex-end">
                  <ButtonGroup>
                    <Button
                      variant="secondary"
                      onClick={handleCancel}
                      title={i18n.t('general.actions.cancel')}
                    />
                    <FormButton
                      control={control}
                      type="submit"
                      variant="primary"
                      isLoading={createLoading || updateLoading}
                      title={i18n.t('general.actions.save')}
                    />
                  </ButtonGroup>
                </HStack>
              </VStack>
            </Card>
          );
        }}
      </Form>
    </DataStatus>
  );
}
